import { useEffect, useState, useRef } from "react";
import { useParams, Link } from "react-router-dom";
import Navbar from "@/components/Navbar";
import { useAuth } from "@/lib/auth";
import { apiClient, readError } from "@/lib/api";
import { toast } from "sonner";
import {
  Award, BadgeCheck, MapPin, Phone, Star, Hammer, FileText,
  Image as ImageIcon, Upload, Trash2, Download, Code2, Eye, TrendingUp, X, ShieldCheck,
} from "lucide-react";

const fmt = (n) => (n || 0).toLocaleString("fr-FR");

export default function ArtisanProfile() {
  const { id } = useParams();
  const { user } = useAuth();
  const [artisan, setArtisan] = useState(null);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [embedHtml, setEmbedHtml] = useState("");
  const [busy, setBusy] = useState(false);
  const [caption, setCaption] = useState("");
  const [lightbox, setLightbox] = useState(null);
  const fileRef = useRef(null);

  const isOwner = user?.id === id;
  const isAdmin = user?.role === "admin";
  const canManage = isOwner || isAdmin;

  const load = async () => {
    try {
      const { data } = await apiClient.get(`/artisans/${id}`);
      setArtisan(data.artisan);
    } catch (e) {
      toast.error(readError(e));
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async () => {
    if (!canManage) return;
    try {
      const { data } = await apiClient.get(`/artisans/${id}/stats`);
      setStats(data);
    } catch {/* silent */}
  };

  const loadEmbed = async () => {
    if (!canManage) return;
    try {
      const { data } = await apiClient.get(`/widget/badge/${id}.html`, { responseType: "text" });
      setEmbedHtml(data);
    } catch {/* silent */}
  };

  useEffect(() => {
    load();
    loadStats();
    loadEmbed();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) {
      toast.error("Fichier trop volumineux (10 Mo max)");
      e.target.value = "";
      return;
    }
    if (!file.type.startsWith("image/")) {
      toast.error("Image uniquement (JPG/PNG/WEBP/HEIC)");
      return;
    }
    setBusy(true);
    const fd = new FormData();
    if (caption.trim()) fd.append("caption", caption.trim());
    fd.append("file", file);
    try {
      await apiClient.post("/me/portfolio", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Photo ajoutée au portfolio");
      setCaption("");
      if (fileRef.current) fileRef.current.value = "";
      await load();
    } catch (err) {
      toast.error(readError(err));
    } finally {
      setBusy(false);
    }
  };

  const removeItem = async (itemId) => {
    if (!window.confirm("Supprimer cette photo ?")) return;
    try {
      await apiClient.delete(`/me/portfolio/${itemId}`);
      toast.success("Supprimé");
      await load();
    } catch (e) {
      toast.error(readError(e));
    }
  };

  const downloadQR = () => {
    const a = document.createElement("a");
    a.href = `${apiClient.defaults.baseURL}/artisans/${id}/qrcard.png`;
    a.download = `carte_batisur_${id.slice(0, 8)}.png`;
    a.target = "_blank";
    document.body.appendChild(a);
    a.click();
    a.remove();
  };

  const copyEmbed = async () => {
    try {
      await navigator.clipboard.writeText(embedHtml);
      toast.success("Code copié dans le presse-papier");
    } catch {
      toast.error("Copie impossible — sélectionnez et copiez manuellement");
    }
  };

  if (loading) return <div className="min-h-screen bg-[#F7F7F9]"><Navbar /><div className="p-10 mono">Chargement…</div></div>;
  if (!artisan) return <div className="min-h-screen bg-[#F7F7F9]"><Navbar /><div className="p-10">Introuvable</div></div>;

  return (
    <div className="min-h-screen bg-[#F7F7F9]">
      <Navbar />
      <div className="max-w-6xl mx-auto px-6 py-10">
        {/* HEADER */}
        <div className="border-2 border-[#0A0A0B] bg-white p-8 shadow-[6px_6px_0_#0A0A0B] relative" data-testid="artisan-header">
          {artisan.is_gold && (
            <div className="absolute top-0 right-0 bg-amber-500 text-[#0A0A0B] px-3 py-1.5 mono text-[10px] uppercase tracking-widest font-semibold flex items-center gap-1">
              <Award className="w-3 h-3" /> Or
            </div>
          )}
          {artisan.is_premium && !artisan.is_gold && (
            <div className="absolute top-0 right-0 bg-[#FF4D00] text-white px-3 py-1.5 mono text-[10px] uppercase tracking-widest font-semibold">★ Premium</div>
          )}
          <div className="mono text-[11px] uppercase tracking-widest text-[#FF4D00]">// Profil artisan Batisur</div>
          <h1 className="display text-5xl tracking-tighter mt-2">{artisan.name}</h1>
          {artisan.company && <div className="text-lg text-zinc-700 mt-1">{artisan.company}</div>}

          <div className="mt-5 flex flex-wrap gap-2 text-xs">
            {artisan.siret_verified && (
              <span className="badge-pill bg-emerald-50 text-emerald-700 border-emerald-400 flex items-center gap-1"><BadgeCheck className="w-3 h-3" /> SIRET vérifié</span>
            )}
            {artisan.has_decennale && (
              <span className="badge-pill bg-blue-50 text-blue-700 border-blue-400 flex items-center gap-1" data-testid="profile-badge-decennale">
                <ShieldCheck className="w-3 h-3" /> Décennale {artisan.decennale_company}{artisan.decennale_valid_until ? ` · ${artisan.decennale_valid_until.split("-").reverse().join("/")}` : ""}
              </span>
            )}
            {artisan.has_rcpro && (
              <span className="badge-pill bg-blue-50 text-blue-700 border-blue-400 flex items-center gap-1" data-testid="profile-badge-rcpro">
                <ShieldCheck className="w-3 h-3" /> RC Pro {artisan.rcpro_company}
              </span>
            )}
            {artisan.google_maps_url && (
              <a
                href={artisan.google_maps_url}
                target="_blank"
                rel="noreferrer"
                className="badge-pill bg-white text-[#0A0A0B] border-[#0A0A0B] flex items-center gap-1 hover:bg-[#0A0A0B] hover:text-white transition-colors"
                data-testid="profile-google-badge"
              >
                <Star className="w-3 h-3 text-[#FF4D00] fill-[#FF4D00]" /> Voir notre fiche Google
              </a>
            )}
            {artisan.certificates_count > 0 && (
              <span className="badge-pill bg-amber-50 text-amber-700 border-amber-400 flex items-center gap-1"><Award className="w-3 h-3" /> {artisan.certificates_count} certificat{artisan.certificates_count > 1 ? "s" : ""}</span>
            )}
            {artisan.completed_missions > 0 && (
              <span className="badge-pill bg-zinc-100 text-zinc-700 border-zinc-300 flex items-center gap-1"><Hammer className="w-3 h-3" /> {artisan.completed_missions} chantier{artisan.completed_missions > 1 ? "s" : ""} terminé{artisan.completed_missions > 1 ? "s" : ""}</span>
            )}
            {artisan.rating_count > 0 && (
              <span className="badge-pill bg-yellow-50 text-yellow-800 border-yellow-300 flex items-center gap-1"><Star className="w-3 h-3" /> {artisan.rating_avg.toFixed(1)} ({artisan.rating_count})</span>
            )}
          </div>

          <div className="mt-5 grid md:grid-cols-2 gap-3 text-sm">
            {artisan.city && <div className="flex items-center gap-2"><MapPin className="w-4 h-4 text-zinc-500" /> {artisan.city}</div>}
            {artisan.phone && <div className="flex items-center gap-2"><Phone className="w-4 h-4 text-zinc-500" /> {artisan.phone}</div>}
            {artisan.siret_denomination && <div className="flex items-center gap-2"><FileText className="w-4 h-4 text-zinc-500" /> {artisan.siret_denomination}</div>}
            {artisan.siret_naf && <div className="flex items-center gap-2 text-zinc-500">NAF {artisan.siret_naf}</div>}
          </div>

          {artisan.specialties?.length > 0 && (
            <div className="mt-5 flex flex-wrap gap-2">
              {artisan.specialties.map((s) => (
                <span key={s} className="badge-pill bg-white border-zinc-300 text-zinc-700">{s}</span>
              ))}
            </div>
          )}

          {artisan.bio && <p className="mt-5 text-sm text-zinc-700 max-w-3xl whitespace-pre-line">{artisan.bio}</p>}
        </div>

        {/* STATS (owner/admin only) */}
        {canManage && stats && (
          <div className="mt-8 border-2 border-[#0A0A0B] bg-white" data-testid="artisan-stats">
            <div className="border-b-2 border-[#0A0A0B] px-6 py-3 flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-[#FF4D00]" />
              <div className="display text-xl">Mes statistiques de visibilité</div>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 divide-x-2 divide-y-2 md:divide-y-0 divide-zinc-200">
              <div className="p-5">
                <div className="mono text-[10px] uppercase tracking-widest text-zinc-500 flex items-center gap-1"><Eye className="w-3 h-3" /> Vues 7j</div>
                <div className="display text-3xl mt-1">{fmt(stats.views_7d)}</div>
              </div>
              <div className="p-5">
                <div className="mono text-[10px] uppercase tracking-widest text-zinc-500">Vues 30j</div>
                <div className="display text-3xl mt-1">{fmt(stats.views_30d)}</div>
              </div>
              <div className="p-5">
                <div className="mono text-[10px] uppercase tracking-widest text-zinc-500">Devis envoyés 30j</div>
                <div className="display text-3xl mt-1">{fmt(stats.quotes_30d)}</div>
              </div>
              <div className="p-5">
                <div className="mono text-[10px] uppercase tracking-widest text-zinc-500">Taux de conversion</div>
                <div className="display text-3xl mt-1 text-emerald-600">{stats.win_rate_pct}%</div>
              </div>
            </div>
          </div>
        )}

        {/* PORTFOLIO */}
        <div className="mt-8 border-2 border-[#0A0A0B] bg-white" data-testid="artisan-portfolio">
          <div className="border-b-2 border-[#0A0A0B] px-6 py-3 flex items-center justify-between flex-wrap gap-3">
            <div className="flex items-center gap-2">
              <ImageIcon className="w-5 h-5 text-[#FF4D00]" />
              <div className="display text-xl">Portfolio · réalisations ({artisan.portfolio?.length || 0}/15)</div>
            </div>
            {canManage && (artisan.is_premium || artisan.is_gold) && (
              <div className="flex items-center gap-2">
                <input
                  type="text"
                  placeholder="Légende (optionnel)"
                  value={caption}
                  onChange={(e) => setCaption(e.target.value)}
                  className="border-2 border-[#0A0A0B] px-3 py-2 text-sm bg-white w-56"
                  data-testid="portfolio-caption"
                />
                <label>
                  <input ref={fileRef} type="file" accept="image/*" onChange={handleFile} disabled={busy} className="hidden" data-testid="portfolio-file" />
                  <span className={`btn-batisur cursor-pointer flex items-center gap-2 ${busy ? "opacity-60 pointer-events-none" : ""}`}>
                    <Upload className="w-4 h-4" /> {busy ? "Envoi…" : "Ajouter"}
                  </span>
                </label>
              </div>
            )}
            {canManage && !artisan.is_premium && !artisan.is_gold && (
              <Link to="/pro/premium" className="text-xs text-[#FF4D00] hover:underline mono uppercase tracking-widest">
                Réservé Premium / Or →
              </Link>
            )}
          </div>
          {artisan.portfolio?.length === 0 ? (
            <div className="p-10 text-center text-zinc-500 text-sm">Aucune réalisation publiée pour le moment.</div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 p-5">
              {artisan.portfolio.map((p) => (
                <div key={p.id} className="relative group border-2 border-[#0A0A0B] overflow-hidden cursor-zoom-in" data-testid={`portfolio-item-${p.id}`}>
                  <img
                    src={`${apiClient.defaults.baseURL}${p.url}`}
                    alt={p.caption || "Réalisation"}
                    className="w-full aspect-square object-cover"
                    onClick={() => setLightbox(p)}
                  />
                  {p.caption && (
                    <div className="absolute bottom-0 left-0 right-0 bg-[#0A0A0B]/90 text-white p-2 text-[10px] mono uppercase tracking-widest leading-tight">
                      {p.caption}
                    </div>
                  )}
                  {canManage && (
                    <button
                      onClick={(e) => { e.stopPropagation(); removeItem(p.id); }}
                      className="absolute top-1 right-1 bg-white border-2 border-[#0A0A0B] p-1 opacity-0 group-hover:opacity-100 transition hover:bg-red-50"
                      data-testid={`portfolio-delete-${p.id}`}
                    >
                      <Trash2 className="w-3 h-3 text-red-600" />
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* MARKETING TOOLS (owner/admin only) */}
        {canManage && (
          <div className="mt-8 grid lg:grid-cols-2 gap-6">
            <div className="border-2 border-[#0A0A0B] bg-white p-6" data-testid="qr-card">
              <div className="mono text-[11px] uppercase tracking-widest text-[#FF4D00]">// Carte de visite Batisur</div>
              <div className="display text-2xl mt-2">Imprimez & collez sur vos véhicules</div>
              <p className="text-sm text-zinc-700 mt-2">
                Une carte de visite professionnelle au format PNG haute qualité avec QR code menant directement à votre profil vérifié Batisur.
                Idéale pour véhicules, signalétiques de chantier, plaquettes ou cartes papier.
              </p>
              <button onClick={downloadQR} className="btn-anthracite mt-5 flex items-center gap-2" data-testid="qr-download">
                <Download className="w-4 h-4" /> Télécharger la carte (PNG)
              </button>
            </div>

            <div className="border-2 border-[#0A0A0B] bg-[#0A0A0B] text-white p-6" data-testid="embed-widget">
              <div className="mono text-[11px] uppercase tracking-widest text-[#FF4D00]">// Badge Batisur pour votre site</div>
              <div className="display text-2xl mt-2">Halo de confiance sur votre vitrine</div>
              <p className="text-sm text-zinc-300 mt-2">
                Collez ce code HTML sur votre site web ou signature email pour afficher votre badge Batisur vérifié — boost immédiat de la confiance des visiteurs.
              </p>
              <pre className="bg-zinc-900 text-zinc-300 mono text-[10px] p-3 mt-4 max-h-32 overflow-auto border border-zinc-700">{embedHtml || "Chargement…"}</pre>
              <button onClick={copyEmbed} className="btn-batisur mt-3 flex items-center gap-2" data-testid="embed-copy">
                <Code2 className="w-4 h-4" /> Copier le code HTML
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Lightbox */}
      {lightbox && (
        <div
          className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-6"
          onClick={() => setLightbox(null)}
        >
          <button onClick={() => setLightbox(null)} className="absolute top-6 right-6 text-white hover:text-[#FF4D00]">
            <X className="w-8 h-8" />
          </button>
          <img src={`${apiClient.defaults.baseURL}${lightbox.url}`} alt={lightbox.caption || ""} className="max-h-full max-w-full object-contain" />
          {lightbox.caption && (
            <div className="absolute bottom-6 left-1/2 -translate-x-1/2 text-white text-sm bg-black/60 px-4 py-2 max-w-md text-center">
              {lightbox.caption}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
